/**
 * NFC Utility for Web NFC API
 * Handles NFC tag scanning in compatible browsers (mainly Chrome on Android)
 */


class NFCUtil {
  constructor() {
    this.isScanning = false
    this.reader = null
    this.abortController = null
    this.currentScanPromise = null
  }

  /**
   * Check if NFC is supported in the current browser/device
   */
  isNFCSupported() {
    return typeof window !== 'undefined' && 'NDEFReader' in window
  }

  /**
   * Check if we have permission to use NFC
   */
  async checkPermission() {
    if (!this.isNFCSupported()) {
      return { granted: false, error: 'NFC not supported' }
    }

    try {
      // Check if we already have permission
      const permission = await navigator.permissions.query({ name: 'nfc' })
      return { 
        granted: permission.state === 'granted',
        state: permission.state,
        error: null
      }
    } catch (error) {
      console.warn('Permission check failed:', error)
      // Fallback: try to scan to check permission implicitly
      return { granted: null, error: error.message }
    }
  }

  /**
   * Request permission to use NFC
   */
  async requestPermission() {
    if (!this.isNFCSupported()) {
      throw new Error('NFC is not supported in this browser/device')
    }

    try {
      this.reader = new NDEFReader()
      
      // Try to scan briefly to trigger permission request
      this.abortController = new AbortController()
      await this.reader.scan({ signal: this.abortController.signal })
      this.abortController.abort()
      
      return { granted: true, error: null }
    } catch (error) {
      if (error.name === 'NotAllowedError') {
        return { granted: false, error: 'NFC permission denied' }
      } else if (error.name === 'NotSupportedError') {
        return { granted: false, error: 'NFC not supported on this device' }
      } else if (error.name === 'AbortError') {
        // This is expected when we abort the scan
        return { granted: true, error: null }
      } else {
        return { granted: false, error: error.message }
      }
    }
  }

  /**
   * Start scanning for NFC tags
   * @param {Object} options - Scan options
   * @param {number} options.timeout - Timeout in milliseconds (default: 30000)
   * @param {Function} options.onReading - Callback for each tag reading
   * @param {Function} options.onError - Callback for errors
   * @returns {Promise} Promise that resolves with tag data or rejects with error
   */
  async startScan(options = {}) {
    const {
      timeout = 30000,
      onReading = null,
      onError = null
    } = options

    if (!this.isNFCSupported()) {
      throw new Error('NFC is not supported in this browser/device')
    }

    if (this.isScanning) {
      throw new Error('NFC scan is already in progress')
    }

    this.isScanning = true
    this.reader = new NDEFReader()
    this.abortController = new AbortController()

    // Set up timeout
    const timeoutId = setTimeout(() => {
      this.stopScan()
      if (this.currentScanPromise) {
        this.currentScanPromise.reject(new Error('NFC scan timeout'))
      }
    }, timeout)

    // Create scan promise
    this.currentScanPromise = {}
    const scanPromise = new Promise((resolve, reject) => {
      this.currentScanPromise.resolve = resolve
      this.currentScanPromise.reject = reject
    })

    try {
      // Set up event listeners
      this.reader.addEventListener('reading', (event) => {
        clearTimeout(timeoutId)
        
        const tagData = this.parseTagData(event)
        
        if (onReading) {
          onReading(tagData)
        }
        
        this.stopScan()
        if (this.currentScanPromise) {
          this.currentScanPromise.resolve(tagData)
        }
      })

      this.reader.addEventListener('readingerror', (event) => {
        clearTimeout(timeoutId)
        const error = new Error(`NFC reading error: ${event.message || 'Unknown error'}`)
        
        if (onError) {
          onError(error)
        }
        
        this.stopScan()
        if (this.currentScanPromise) {
          this.currentScanPromise.reject(error)
        }
      })

      // Start scanning
      await this.reader.scan({ signal: this.abortController.signal })
      
      return scanPromise
    } catch (error) {
      clearTimeout(timeoutId)
      this.stopScan()
      
      if (error.name === 'NotAllowedError') {
        throw new Error('NFC permission denied. Please allow NFC access in your browser settings.')
      } else if (error.name === 'NotSupportedError') {
        throw new Error('NFC is not supported on this device.')
      } else if (error.name === 'AbortError') {
        throw new Error('NFC scan was cancelled.')
      } else {
        throw new Error(`NFC scan failed: ${error.message}`)
      }
    }
  }

  /**
   * Stop the current NFC scan
   */
  stopScan() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
    
    this.isScanning = false
    this.reader = null
    this.currentScanPromise = null
  }

  /**
   * Parse tag data from NFC reading event
   * @param {Event} event - NFC reading event
   * @returns {Object} Parsed tag data
   */
  parseTagData(event) {
    const { message, serialNumber } = event

    // Convert serial number to hex string
    const tagId = Array.from(new Uint8Array(serialNumber))
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase()

    // Parse NDEF records if available
    let records = []
    if (message && message.records) {
      records = message.records.map(record => {
        const decoder = new TextDecoder()
        
        return {
          recordType: record.recordType,
          mediaType: record.mediaType,
          id: record.id,
          data: record.data ? decoder.decode(record.data) : null,
          encoding: record.encoding || 'utf-8'
        }
      })
    }

    return {
      tagId,
      serialNumber: Array.from(new Uint8Array(serialNumber)),
      message: {
        records
      },
      timestamp: new Date().toISOString(),
      scanMethod: 'nfc_scan'
    }
  }

  /**
   * Write data to an NFC tag
   * @param {Object} data - Data to write
   * @param {string} data.text - Text to write
   * @param {string} data.url - URL to write
   * @param {Object} options - Write options
   * @returns {Promise} Promise that resolves when write is complete
   */
  async writeTag(data, options = {}) {
    if (!this.isNFCSupported()) {
      throw new Error('NFC is not supported in this browser/device')
    }

    const { timeout = 10000 } = options

    try {
      const writer = new NDEFReader()
      
      // Prepare NDEF message
      const records = []
      
      if (data.text) {
        records.push({
          recordType: 'text',
          data: data.text
        })
      }
      
      if (data.url) {
        records.push({
          recordType: 'url',
          data: data.url
        })
      }

      // Add custom application data if needed
      if (data.appData) {
        records.push({
          recordType: 'application/json',
          data: JSON.stringify(data.appData)
        })
      }

      const abortController = new AbortController()
      const timeoutId = setTimeout(() => {
        abortController.abort()
      }, timeout)

      await writer.write({ records }, { signal: abortController.signal })
      
      clearTimeout(timeoutId)
      return true
    } catch (error) {
      if (error.name === 'NotAllowedError') {
        throw new Error('NFC write permission denied.')
      } else if (error.name === 'NotSupportedError') {
        throw new Error('NFC write is not supported on this device.')
      } else if (error.name === 'AbortError') {
        throw new Error('NFC write timeout.')
      } else {
        throw new Error(`NFC write failed: ${error.message}`)
      }
    }
  }

  /**
   * Get detailed NFC capabilities
   */
  getCapabilities() {
    return {
      isSupported: this.isNFCSupported(),
      canRead: this.isNFCSupported(),
      canWrite: this.isNFCSupported(),
      userAgent: navigator.userAgent,
      isAndroid: /Android/i.test(navigator.userAgent),
      isChrome: /Chrome/i.test(navigator.userAgent),
      recommendation: this.getRecommendation()
    }
  }

  /**
   * Get user recommendation based on browser/device
   */
  getRecommendation() {
    const isAndroid = /Android/i.test(navigator.userAgent)
    const isChrome = /Chrome/i.test(navigator.userAgent)
    
    if (!this.isNFCSupported()) {
      if (isAndroid) {
        return 'NFC is not supported. Try using Chrome on Android with NFC enabled.'
      } else {
        return 'NFC is only supported on Android devices with Chrome browser.'
      }
    }
    
    if (isAndroid && isChrome) {
      return 'NFC is supported! Make sure NFC is enabled in device settings.'
    }
    
    return 'NFC support detected, but compatibility may vary.'
  }
}

// Create singleton instance
const nfcUtil = new NFCUtil()

export default nfcUtil